import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '../../../../../app/store/utils/baseQueryWithReauth';
import {
  SYSTEM_BILLING_BASE,
  unwrapApiData,
} from '../../../shared/infrastructure/api/unwrapApiData';
import type {
  BillingPlan,
  CreateBillingPlanInput,
  UpdateBillingPlanInput,
} from '../../domain/plan.types';

export const billingPlansApi = createApi({
  reducerPath: 'billingPlansApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BillingPlan'],
  endpoints: build => ({
    getPlans: build.query<BillingPlan[], void>({
      query: () => ({
        url: `${SYSTEM_BILLING_BASE}/plans/list`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<BillingPlan[]>(response),
      providesTags: ['BillingPlan'],
    }),

    createPlan: build.mutation<unknown, CreateBillingPlanInput>({
      query: body => ({
        url: `${SYSTEM_BILLING_BASE}/plans/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BillingPlan'],
    }),

    getPlanById: build.query<BillingPlan, number>({
      query: planId => ({
        url: `${SYSTEM_BILLING_BASE}/plans/${planId}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<BillingPlan>(response),
      providesTags: (_result, _error, planId) => [
        { type: 'BillingPlan', id: planId },
      ],
    }),

    updatePlan: build.mutation<BillingPlan, { id: number; body: UpdateBillingPlanInput }>({
      query: ({ id, body }) => ({
        url: `${SYSTEM_BILLING_BASE}/plans/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<BillingPlan>(response),
      invalidatesTags: (_result, _error, { id }) => [
        'BillingPlan',
        { type: 'BillingPlan', id },
      ],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
} = billingPlansApi;
